import { DigitalMarketingPlan } from '../types';

export const digitalMarketingPlans: DigitalMarketingPlan[] = [
  {
    id: 'starter-plan',
    name: 'Starter Visibility & Local Niche Plan',
    tier: 'Starter / Low Business',
    headline: 'Minimal Rates Base for Informative & Early-Stage Websites',
    description: 'A cost-effective, high-yield digital marketing plan tailored for informative websites, local service providers, and startups with low traffic who need quality local leads without paying massive agency retainers.',
    monthlyUsd: 199,
    monthlyInr: 14999,
    keywordsCount: '10 - 15 High-Intent Targeted Keywords',
    suitabilityCondition: 'Informative Website & Low Business Volume',
    suitabilityScenario: 'Best if you run an informational site, personal practice, or newly launched business with limited initial traffic and want to generate steady inbound calls on a minimal monthly budget.',
    badge: 'Minimal Rate Entry',
    googleAdsPackage: {
      campaignTypes: [
        'Google Search Ads (1 - 2 Hyper-Targeted Ad Groups)',
        'Local Radius & Geotargeted Search Campaigns',
        'Google Click-to-Call Extensions'
      ],
      features: [
        'Precise negative keyword filtration to protect your budget from irrelevant clicks',
        'Call tracking & contact form conversion setup in Google Analytics 4',
        'Ad headline and description copy crafted for high click-through rates (CTR)',
        'Keyword match-type optimization (Exact + Phrase match focus)',
        'Ad spend management for modest daily budgets ($5 - $15/day)'
      ],
      recommendedAdSpend: '$150 - $400 / month (client budget)'
    },
    seoFeatures: [
      'Targeted keyword mapping for 10-15 buyer-intent search phrases',
      'On-page SEO optimization for up to 10 primary website pages',
      'Meta tags, titles, alt text, and OpenGraph social preview optimization',
      'Google Search Console & XML sitemap submission',
      'Google Business Profile (GBP) local optimization & NAP consistency',
      'Technical health check & mobile responsiveness audit'
    ],
    deliverables: [
      'Monthly Keyword Ranking Telemetry Report',
      'Google Ads Clicks, Impressions & Cost-Per-Lead (CPL) Breakdown',
      'Google Analytics 4 traffic & user engagement overview',
      'Email & WhatsApp monthly strategy support'
    ]
  },
  {
    id: 'growth-plan',
    name: 'Growth & Regional Expansion Plan',
    tier: 'Growth / Mid-Market',
    headline: 'Accelerate Lead Velocity & Outrank Regional Competitors',
    description: 'Engineered for growing service firms, multi-branch practices, and mid-sized businesses with steady operations who want to dominate their regional market and drive consistent qualified sales consultations.',
    monthlyUsd: 449,
    monthlyInr: 29999,
    keywordsCount: '25 - 35 Competitive & Commercial Keywords',
    suitabilityCondition: 'Growing Service Firm & Multi-Location Practice',
    suitabilityScenario: 'Best if your website already has basic traction and you want to expand beyond your local neighborhood, capture competitive search terms, and double your qualified inbound leads.',
    isPopular: true,
    badge: 'Most Popular for Growth',
    googleAdsPackage: {
      campaignTypes: [
        'Advanced Google Search Ads (4 - 6 Themed Ad Groups)',
        'Google Display Network (GDN) Visual Retargeting',
        'Competitor Keyword Conquesting Campaigns'
      ],
      features: [
        'Target CPA (Cost per Acquisition) automated smart bidding optimization',
        'Continuous A/B split-testing of ad variations, extensions, and landing pages',
        'Audience remarketing list for search ads (RLSA) to re-engage past visitors',
        'Lead form extension integration with direct CRM webhook alerts',
        'Ad spend management for intermediate growth budgets ($20 - $50/day)'
      ],
      recommendedAdSpend: '$600 - $1,500 / month (client budget)'
    },
    seoFeatures: [
      'Comprehensive keyword strategy covering 25-35 high-commercial keywords',
      'Technical SEO overhaul, Core Web Vitals profiling, and site speed enhancement',
      '2 High-quality, SEO-optimized authority blog articles or service pages/month',
      'Rich schema markup (LocalBusiness, Service, FAQPage, BreadcrumbList)',
      'High-authority backlink acquisition & contextual niche citations',
      'Competitor keyword gap analysis & ranking opportunity radar'
    ],
    deliverables: [
      'Bi-Weekly Keyword Movement & Lead Attribution Dashboard',
      'Google Ads ROAS & Customer Acquisition Cost (CAC) Tracking',
      'Conversion Rate Optimization (CRO) recommendations',
      'Bi-weekly 30-minute strategic consultation with lead marketing strategist'
    ]
  },
  {
    id: 'ecommerce-plan',
    name: 'E-Commerce Market Dominance Plan',
    tier: 'E-Commerce / Big Market Reach',
    headline: 'Massive Consumer Reach, Google Shopping & High-ROAS Scaling',
    description: 'The ultimate performance marketing engine for online stores, Shopify/WooCommerce brands, and retailers with large product inventories targeting nationwide or international buyers.',
    monthlyUsd: 799,
    monthlyInr: 49999,
    keywordsCount: '60 - 100+ Transactional & Product Keywords',
    suitabilityCondition: 'E-Commerce Store & Large Catalog Market Reach',
    suitabilityScenario: 'Best if you sell physical or digital goods online, have dozens to hundreds of SKUs, and require top Google Shopping visibility, national market reach, and profitable ROAS-based revenue scaling.',
    badge: 'Big Market Reach',
    googleAdsPackage: {
      campaignTypes: [
        'Google Shopping Ads & Performance Max (PMax) Campaigns',
        'Dynamic Product Remarketing across YouTube, Gmail & Display',
        'High-Intent Product Long-Tail Search Ad Campaigns',
        'Seasonal Sales Blitzes (Black Friday, Holiday & Clearance)'
      ],
      features: [
        'Google Merchant Center feed setup, sync, title enhancement & error resolution',
        'ROAS-driven Smart Bidding (Target ROAS & Maximize Conversion Value)',
        'Dynamic product carousel ads showing exact products viewed by shoppers',
        'Negative product filtering (cutting spend on low-margin or out-of-stock items)',
        'Ad spend management for high-growth budgets ($50 - $200+/day)'
      ],
      recommendedAdSpend: '$1,500 - $5,000+ / month (scalable with ROAS)'
    },
    seoFeatures: [
      'Extensive keyword architecture for 60-100+ product categories and SKU searches',
      'Complete E-commerce structured data (JSON-LD Product, Offer, Reviews, InStock)',
      'Faceted navigation, crawl budget optimization & filter indexing control',
      'Category page optimization & rich transactional buyer intent mapping',
      'Mobile checkout UX and site speed optimization for higher checkout conversion',
      'Brand mentions, digital PR & authoritative e-commerce backlink acquisition'
    ],
    deliverables: [
      'Real-Time Live E-Commerce Performance Dashboard (ROAS, Revenue, Orders)',
      'Google Merchant Center Product Health & Feed Diagnostics Report',
      'Shopping Cart Abandonment & Funnel Drop-off Analysis',
      'Dedicated Senior E-Commerce Growth Architect & Weekly Strategy Sprints'
    ]
  }
];

export const situationComparisonData = [
  {
    situation: 'Informative Website & Low Business / Startup',
    problem: 'Low traffic, limited marketing budget, need local inquiries without high fixed expenses.',
    recommendedPlan: 'Starter Visibility & Local Niche Plan',
    keyFocus: '10-15 High-Intent Local Keywords + Local Google Search Ads',
    rate: '$199 / mo (₹14,999)',
    highlightColor: 'blue'
  },
  {
    situation: 'Growing Business / Regional Service Firm',
    problem: 'Moderate baseline traffic, fierce regional rivals, desire to scale lead volume predictably.',
    recommendedPlan: 'Growth & Regional Expansion Plan',
    keyFocus: '25-35 Commercial Keywords + Search & Display Ads + Content SEO',
    rate: '$449 / mo (₹29,999)',
    highlightColor: 'orange'
  },
  {
    situation: 'E-Commerce Website & Big Market Reach',
    problem: 'Hundreds of products, large multi-regional audience, high competition in retail queries.',
    recommendedPlan: 'E-Commerce Market Dominance Plan',
    keyFocus: '60-100+ Product Keywords + Google Shopping & Performance Max (PMax)',
    rate: '$799 / mo (₹49,999)',
    highlightColor: 'purple'
  }
];
